// Host-facing HTTP implementation of AudioGenApi. Firebase-free: auth + base URL
// come from the injected BackendHttpDeps. Do NOT import from barrel exports
// (audioGen/index.ts).

import {
  AudioGenApi,
  AudioGenParams,
  AudioGenResult,
  AudioGenJobEvent,
  ListVoicesResult,
  DesignVoicesParams,
  DesignVoicesResult,
  SaveDesignedVoiceParams,
  SaveDesignedVoiceResult,
} from './AudioGenApi';
import { RundotApiError, RateLimitedError } from '../errors';
import { resolveGameSessionToken } from '../http/backendHttpDeps';
import type { BackendHttpDeps } from '../http/backendHttpDeps';

export class HttpAudioGenApi implements AudioGenApi {
  private readonly appId: string;
  private readonly deps: BackendHttpDeps;

  constructor(appId: string, deps: BackendHttpDeps) {
    this.appId = appId;
    this.deps = deps;
  }

  private async authedFetch(path: string, method: string, body?: unknown): Promise<Response> {
    const idToken = await this.deps.getIdToken();
    const baseUrl = this.deps.getCloudRunUrl();
    const url = `${baseUrl}/v1/audiogen/${this.appId}/${path}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    };

    const gst = await resolveGameSessionToken(this.deps.getGameSessionToken);
    if (gst) headers['x-h5-game-session'] = gst;

    const response = await fetch(url, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

    if (!response.ok) {
      if (response.status === 429) {
        const retryAfterSec = parseInt(response.headers.get('Retry-After') ?? '60', 10);
        throw new RateLimitedError(retryAfterSec * 1000);
      }
      const errorText = await response.text();
      let errorMessage = `Audio generation failed (${response.status})`;
      let errorCode = 'UNKNOWN';
      try {
        const errorJson = JSON.parse(errorText) as Record<string, unknown>;
        errorMessage = (errorJson.error ?? errorJson.message ?? errorMessage) as string;
        if (typeof errorJson.code === 'string') errorCode = errorJson.code;
      } catch {
        if (errorText) errorMessage = errorText;
      }
      throw new RundotApiError(errorCode, errorMessage, response.status);
    }

    return response;
  }

  async generate(params: AudioGenParams): Promise<AudioGenResult> {
    const response = await this.authedFetch('generate', 'POST', params);
    return response.json();
  }

  async getCompletedJobs(): Promise<AudioGenJobEvent[]> {
    return [];
  }

  async listVoices(): Promise<ListVoicesResult> {
    const response = await this.authedFetch('voices', 'GET');
    return response.json();
  }

  async designVoices(params: DesignVoicesParams): Promise<DesignVoicesResult> {
    const response = await this.authedFetch('voices/design', 'POST', params);
    return response.json();
  }

  async saveDesignedVoice(params: SaveDesignedVoiceParams): Promise<SaveDesignedVoiceResult> {
    const response = await this.authedFetch('voices/save', 'POST', params);
    return response.json();
  }
}
